// Venue selector for the host event create form.
//
// Dialog flow:
//   - User types a query (debounced) → server proxies to Google Places
//   - Results list on the left; selected result shown with a map preview
//     on the right (single-column on mobile)
//   - "Use this location" returns the place to the caller and closes
//
// Map preview is a static PNG fetched via /api/host/locations/static-map,
// which keeps the Google Maps API key on the server. The image URL carries
// our auth via the same path as other authenticated API calls (Vite proxy
// in dev; CORS-allowed origin + cookie/Bearer in prod via the api helper).
//
// We can't easily set Authorization on a plain <img> tag, so the static
// map endpoint is intentionally cookie-auth-friendly. If you ever switch
// the project to pure Bearer-only auth, change this to a blob fetch.

import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Search, X } from "lucide-react";
import {
  useLocationSearch,
  type LocationResult,
} from "../api/useLocationSearch";
import { SheetModal } from "@/components/SheetModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (place: LocationResult) => void;
}

export function LocationSelectModal({ isOpen, onClose, onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const search = useLocationSearch();
  const debounceRef = useRef<number | null>(null);

  // Reset on open.
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedId(null);
      search.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Debounced search.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!query.trim()) {
      search.reset();
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      search.mutate(query.trim());
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const results = search.data?.success ? search.data.data ?? [] : [];
  const selected = results.find((r) => r.placeId === selectedId) ?? null;

  // Auto-select the first result when results come in.
  useEffect(() => {
    if (results.length > 0 && !selectedId) {
      setSelectedId(results[0].placeId);
    }
  }, [results, selectedId]);

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-lg max-h-[85vh]">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
        <h2 className="text-lg font-semibold flex-1">Choose a venue</h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedId(null);
            }}
            placeholder="Search for a place or address…"
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 border-0 outline-none text-sm placeholder:text-neutral-400 focus:ring-2 focus:ring-primary-500/40"
          />
        </div>
      </div>

      {/* Results list */}
      <div className="flex-1 overflow-y-auto p-2 min-h-0">
        {search.isPending && (
          <div className="flex items-center justify-center py-8 text-sm text-neutral-500 dark:text-neutral-400">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Searching…
          </div>
        )}
        {!search.isPending && query.trim() === "" && (
          <div className="text-center text-sm text-neutral-500 dark:text-neutral-400 py-8">
            Type to search venues
          </div>
        )}
        {!search.isPending && query.trim() !== "" && results.length === 0 && (
          <div className="text-center text-sm text-neutral-500 dark:text-neutral-400 py-8">
            No results for "{query}"
          </div>
        )}
        {results.map((r) => {
          const active = r.placeId === selectedId;
          return (
            <button
              type="button"
              key={r.placeId}
              onClick={() => setSelectedId(r.placeId)}
              className={`
                w-full text-left px-3 py-3 rounded-lg flex items-start gap-3 transition-colors
                ${active ? "bg-primary-500/10" : "hover:bg-neutral-100 dark:hover:bg-neutral-800"}
              `}
            >
              <MapPin
                className={`w-4 h-4 mt-1 shrink-0 ${active ? "text-primary-500" : "text-neutral-400"}`}
              />
              <div className="min-w-0">
                <div className="font-medium truncate">{r.name}</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                  {r.address}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-between gap-3 shrink-0">
        {selected ? (
          <div className="min-w-0 text-sm">
            <div className="font-medium truncate">{selected.name}</div>
            <div className="text-neutral-500 dark:text-neutral-400 truncate">
              {selected.address}
            </div>
          </div>
        ) : (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">
            Nothing selected
          </div>
        )}
        <button
          type="button"
          disabled={!selected}
          onClick={() => {
            if (selected) {
              onSelect(selected);
              onClose();
            }
          }}
          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          Use this venue
        </button>
      </div>
    </SheetModal>
  );
}
